import React, { useEffect, useState } from 'react';
import {
    ColumnHeaderDetails,
    ColumnHeaderTitle,
    ColumnHeaderWrapper,
    ColumnSubHeader,
    ColumnSubHeaderTitle,
    RobCustomTable,
    RobTableColumn,
    RobTableWrapper,
} from './BunkerRob.styles';
import { AolGroup } from 'src/types/ViewModel/AolItem';
import { useBunkerContext } from 'src/context/BunkerContext';
import { FuelRobVM } from 'src/types/ViewModel/Bunkering/FuelRobVM';
import { FuelROB } from 'src/types/ViewModel/Bunkering/FuelROB';
import { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { UUID } from 'crypto';
import { FuelRobColumn } from 'src/types/ViewModel/Bunkering/FuelRobColumn';
import BunkerRobTank from './BunkerRobTank';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { useAolStore } from 'src/context/AolContext';
import BunkerRobTotal from './BunkerRobTotal';
import { BunkerReceived } from 'src/types/ViewModel/Bunkering/BunkerReceived';
import BunkerRobColumnHeader from './BunkerRobColumnHeader';
import TankContentRows from './TankContentRows';
import PreviousRobRows from './PreviousRobRows';
import { getContentsInTankAtDate } from '../BunkeringUtils';
import { addDateHours } from 'odfjell-date-time-utils/dateTimeUtils';
import BunkerLossContainer from './BunkerLossContainer';

interface BunkerRobTableProps {
    fuelRobVM: FuelRobVM;
    aolGroup: AolGroup;
    selectedBunkeringDate: Date;
    fuelTanks?: Tank[];
    changeFuelRob: (bunkeringGuid: UUID, tankId: number, fuelRobColumn: FuelRobColumn, robValue: number) => void;
    changeSoundingVerified: (bunkeringGuid: UUID, tankId: number, column: FuelRobColumn, soundingVerified: boolean) => void;
    changeDebunkering: (tankId: number, column: FuelRobColumn, previousColumn: FuelRobColumn, bunkerReceivedMt: number, oldValue: number) => void;
    transferFuel: (column: FuelRobColumn, bunkeringGuid: UUID, fromTankId: number, toTankId: number, transferValue: number) => void;
}

const BunkerRobTable: React.FC<BunkerRobTableProps> = ({
    fuelRobVM,
    aolGroup,
    selectedBunkeringDate,
    fuelTanks,
    changeFuelRob,
    changeSoundingVerified,
    changeDebunkering,
    transferFuel,
}) => {
    const { bunkerings, fuelRobData, bunkerLossData } = useBunkerContext();
    const { aolDataItems } = useAolStore();
    const [bunkerLossModalOpen, setbunkerLossModalOpen] = useState<boolean>(false);
    const [bunkerLossColumn, setbunkerLossColumn] = useState<FuelRobColumn>(null);
    const [VM, setVM] = useState(fuelRobVM);

    useEffect(() => {
        setVM(fuelRobVM);
    }, [fuelRobVM]);

    const onChange = (changeObject: InputChangeData, rob: FuelROB, column: FuelRobColumn) => {
        const value = parseFloat(changeObject.event.target.value);
        changeFuelRob(rob.bunkeringGuid, rob.tankId, column, value);
    };

    const onToggleSoundingVerified = (changeObject: InputChangeData, rob: FuelROB, column: FuelRobColumn) => {
        const value = changeObject.event.target.checked;
        changeSoundingVerified(rob.bunkeringGuid, rob.tankId, column, value);
    };

    const onChangeDebunkering = (
        changeObject: InputChangeData,
        debunkerValue: BunkerReceived,
        column: FuelRobColumn,
        previousColumn: FuelRobColumn,
        oldValue: number
    ) => {
        changeDebunkering(debunkerValue.tankId, column, previousColumn, parseFloat(changeObject.event.target.value), oldValue);
    };

    const handleOpenbunkerLossModal = (column: FuelRobColumn) => {
        setbunkerLossColumn(column);
        setbunkerLossModalOpen(true);
    };

    return (
        <RobTableWrapper>
            {bunkerLossModalOpen && (
                <BunkerLossContainer column={bunkerLossColumn} selectedDate={selectedBunkeringDate} onClose={() => setbunkerLossModalOpen(false)} />
            )}
            <RobCustomTable>
                <RobTableColumn>
                    <ColumnHeaderWrapper>
                        <ColumnHeaderTitle>Tank</ColumnHeaderTitle>
                        <ColumnHeaderDetails> </ColumnHeaderDetails>
                        <ColumnSubHeader>
                            <ColumnSubHeaderTitle></ColumnSubHeaderTitle>
                        </ColumnSubHeader>
                    </ColumnHeaderWrapper>
                    {fuelTanks?.map((tank) => (
                        <TankContentRows
                            key={`${tank.tankId}_tank_name_row`}
                            tank={tank}
                            tankNameColumn={true}
                            contentsInTank={getContentsInTankAtDate(
                                bunkerings,
                                tank.tankId,
                                addDateHours(selectedBunkeringDate, 12),
                                aolGroup,
                                aolDataItems,
                                fuelRobData
                            )}
                            fuelRobVM={fuelRobVM}
                        />
                    ))}
                </RobTableColumn>
                <RobTableColumn>
                    <ColumnHeaderWrapper>
                        <ColumnHeaderTitle>Contents</ColumnHeaderTitle>
                        <ColumnHeaderDetails> </ColumnHeaderDetails>
                        <ColumnSubHeader>
                            <ColumnSubHeaderTitle></ColumnSubHeaderTitle>
                        </ColumnSubHeader>
                    </ColumnHeaderWrapper>
                    {fuelTanks?.map((tank) => (
                        <TankContentRows
                            key={`${tank.tankId}_content_row`}
                            tank={tank}
                            tankNameColumn={false}
                            contentsInTank={getContentsInTankAtDate(
                                bunkerings,
                                tank.tankId,
                                addDateHours(selectedBunkeringDate, 12),
                                aolGroup,
                                aolDataItems,
                                fuelRobData
                            )}
                            fuelRobVM={fuelRobVM}
                        />
                    ))}
                </RobTableColumn>
                <RobTableColumn key={`previous_rob_column`}>
                    <ColumnHeaderWrapper>
                        <ColumnHeaderTitle>Previous</ColumnHeaderTitle>
                        <ColumnHeaderDetails> </ColumnHeaderDetails>
                        <ColumnSubHeader>
                            <ColumnSubHeaderTitle>ROB</ColumnSubHeaderTitle>
                        </ColumnSubHeader>
                    </ColumnHeaderWrapper>
                    {fuelTanks?.map((tank) => (
                        <PreviousRobRows
                            key={`${tank.tankId}_prev_rob`}
                            tankId={tank.tankId}
                            fuelRobVM={fuelRobVM}
                            previousRobs={fuelRobVM?.previousRobColumn?.fuelRobs}
                        />
                    ))}
                </RobTableColumn>
                {VM?.fuelRobColumns?.map((column, i) => (
                    <RobTableColumn
                        key={`column_${i}_${column?.fuelRobs?.length}`}
                        displayLossColumn={bunkerLossData.some((l) => l.noonReportId === column?.noonReportId)}
                        debunkeringColumn={!!column.debunkeringEventId}
                    >
                        <BunkerRobColumnHeader
                            robColumn={column}
                            aolGroup={aolGroup}
                            openbunkerLossModal={handleOpenbunkerLossModal}
                            displayLossColumn={bunkerLossData.some((l) => l.noonReportId === column?.noonReportId)}
                        />
                        {fuelTanks?.map((tank) => (
                            <BunkerRobTank
                                vm={VM}
                                key={`${tank.tankId}_${column.bunkeringGuid}_${column.noonReportId}_${column?.fuelRobs?.length}`}
                                currentColumn={column}
                                previousColumn={i === 0 ? fuelRobVM.previousRobColumn : fuelRobVM.fuelRobColumns[i - 1]}
                                tankId={tank.tankId}
                                fuelTanks={fuelTanks}
                                onChange={onChange}
                                handleToggleSoundingVerified={onToggleSoundingVerified}
                                onChangeDebunkering={onChangeDebunkering}
                                onTransferFuel={transferFuel}
                            />
                        ))}
                    </RobTableColumn>
                ))}
            </RobCustomTable>
            <BunkerRobTotal fuelRobVM={fuelRobVM} />
        </RobTableWrapper>
    );
};

export default BunkerRobTable;
